/**
 * Unified prompt draft editor (PROC-75).
 *
 * The left-column panel on the evaluations page. One form covering every
 * yaml-editable field, one Save button. Replaces the old
 * ``DraftEditor`` (instructions-only) + ``StructuralFieldsPanel``
 * (everything else) split that made users think about two save flows.
 *
 * Most fields persist to ``Prompt.draft_overrides`` on the api-django
 * side. Save composes the merged dict; the footer's Review-draft button
 * navigates to ``/prompts/:key/review-draft`` where the composed diff is
 * shown and the actual PR opens.
 *
 * ``sample_output``/``response_schema`` are the exception (PROC-190):
 * both are DB-only columns everywhere else in this codebase (Rio's
 * tools, the Prompt Constructor) and write straight to the live
 * ``Prompt`` row via ``updatePrompt`` on Save — no draft stage, no PR.
 * Staging them here used to let a stale draft value permanently mask a
 * live update made through any other path.
 */
import { ChevronDownIcon, ChevronRightIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Heading, Paragraph } from "@fronterahealth/frontera-ui-components";

import type { GetPromptDetailsOutput, GetPromptDraftOutput as PromptDraft } from "@api/rest/types-and-hooks";
import {
  deletePromptDraft as deletePromptDraftRaw,
  getGetPromptDetailsQueryKey,
  putPromptDraft as putPromptDraftRaw,
  updatePrompt as updatePromptRaw,
} from "@api/rest/types-and-hooks";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError } from "@components/notifications/notifications";
import { NewAssessmentInputDialog } from "@pages/Prompts/NewAssessmentInputDialog";
import { PromptInputsSelect } from "@pages/Prompts/PromptInputsSelect";
import { PromptRoleSelect } from "@pages/Prompts/PromptRoleSelect";
import { PromptTechnicalInstructionsSelect } from "@pages/Prompts/PromptTechnicalInstructionsSelect";
import { PromptTemplatedInstructionsSelect } from "@pages/Prompts/PromptTemplatedInstructionsSelect";
import type { DraftOverrides } from "@pages/Prompts/promptDraftFields";
import { hasRealDraft, partialFailureResetBaseline, splitDirtyFields } from "@pages/Prompts/promptDraftFields";
import { TemplateSaveFooter } from "@pages/Templates/TemplateSaveFooter";

interface PromptDraftEditorProps {
  prompt: GetPromptDetailsOutput;
  draft: PromptDraft | undefined;
  promptKey: string;
  /** Fires whenever any field is dirty. Parent uses this to gate the
   *  "Run test" button on unsaved edits — the server-side runner reads
   *  the SAVED draft, so running with a dirty buffer would produce
   *  output that doesn't match the visible form content. */
  onDirtyChange?: (dirty: boolean) => void;
}

interface FormShape {
  role: string;
  templated_instructions: string;
  instructions: string;
  technical_instructions: string;
  sample_output: string;
  response_schema: string;
  input_keys: string[];
}

// ``DraftOverrides`` (imported from promptDraftFields) deliberately
// has no response_schema/sample_output keys — both are DB-only
// columns everywhere else in this codebase and are always written
// straight to the live ``Prompt`` row via ``updatePrompt``, never
// staged here. See promptDraftFields.ts for why and PROC-190 for the
// bug this fixes.

function initialInputKeys(
  overrideInputs: { key: string }[] | undefined,
  yamlInputKeys: string[] | undefined,
): string[] {
  // Prefer the raw yaml ``input_keys`` list (nested leaves like
  // ``biographic_info.name`` / ``interviews.other`` intact) over the
  // flattened ``inputs`` display list, which collapses siblings under
  // the parent input class and would write the parent back on save —
  // losing per-leaf granularity in the generated yaml.
  const raw = overrideInputs ? overrideInputs.map((i) => i.key) : (yamlInputKeys ?? []);
  // Dedupe while preserving first-seen order. Draft data can carry
  // duplicates from older client bugs; without this the multi-select
  // renders repeated rows and Save writes the duplicates back.
  return Array.from(new Set(raw));
}

function stringifySchema(schema: unknown): string {
  if (schema == null) return "";
  if (typeof schema === "string") return schema;
  try {
    return JSON.stringify(schema, null, 2);
  } catch {
    return "";
  }
}

/** Draft query key must match PromptTestBed's `draftQueryKey`. */
function draftQueryKey(key: string) {
  return ["prompt-draft", key] as const;
}

/** Collapsible section wrapper for secondary draft fields. Reduces the
 *  vertical footprint of the left panel — empty fields collapse by
 *  default, populated ones stay open on first render so the user sees
 *  what's there without hunting. Local state; no persistence — a page
 *  reload resets each section to its content-based default. */
const CollapsibleField: React.FC<{
  label: string;
  hint?: ReactNode;
  defaultOpen: boolean;
  collapsedPreview?: ReactNode;
  children: ReactNode;
}> = ({ label, hint, defaultOpen, collapsedPreview, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-left cursor-pointer hover:text-text-primary group"
      >
        <Chevron className="h-3.5 w-3.5 text-text-tertiary group-hover:text-text-secondary shrink-0" />
        <span className="text-xs font-semibold uppercase tracking-wide text-text-secondary">{label}</span>
        {hint && <span className="text-xs font-normal text-text-tertiary normal-case">{hint}</span>}
        {!open && collapsedPreview && (
          <span className="ml-2 truncate text-xs text-text-tertiary italic normal-case">{collapsedPreview}</span>
        )}
      </button>
      {open && <div className="pl-5">{children}</div>}
    </div>
  );
};

export const PromptDraftEditor: React.FC<PromptDraftEditorProps> = ({ prompt, draft, promptKey, onDirtyChange }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [isNewAssessmentOpen, setIsNewAssessmentOpen] = useState(false);
  // Bumped after every successful save. Passed to `PromptInputsSelect`
  // so its search-query box clears on save — otherwise the user is left
  // staring at a filtered view after committing the change.
  const [saveVersion, setSaveVersion] = useState(0);

  const overrides = (draft?.draft_overrides ?? {}) as DraftOverrides;

  const defaultValues = useMemo<FormShape>(() => {
    // For nullable string fields, a draft that explicitly saved
    // ``null`` means "clear this field" — NOT "fall back to yaml."
    // ``??`` would silently revert to yaml on reload; the ``in`` check
    // preserves the explicit-null intent.
    const pickOverride = <T,>(field: keyof DraftOverrides, yamlFallback: T | null | undefined): T | null =>
      field in overrides ? ((overrides[field] as T | null) ?? null) : ((yamlFallback ?? null) as T | null);

    return {
      role: pickOverride<string>("role", prompt.role) ?? "",
      templated_instructions: pickOverride<string>("templated_instructions", prompt.templated_instructions) ?? "",
      instructions: pickOverride<string>("instructions", prompt.instructions) ?? "",
      technical_instructions: pickOverride<string>("technical_instructions", prompt.technical_instructions) ?? "",
      // DB-only, live-write fields — always the current row value,
      // never a draft override (see the DraftOverrides comment above).
      sample_output: prompt.sample_output ?? "",
      response_schema: stringifySchema(prompt.response_schema),
      input_keys: initialInputKeys(overrides.inputs, prompt.input_keys),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- overrides derived from draft
  }, [prompt, draft]);

  // Build fresh yaml-source defaults on demand (used by the Discard flow
  // to revert every field back to yaml). Kept as a callable rather than
  // memoized so the closure always sees the latest prompt prop.
  const yamlSourceValues = (): FormShape => ({
    role: prompt.role ?? "",
    templated_instructions: prompt.templated_instructions ?? "",
    instructions: prompt.instructions ?? "",
    technical_instructions: prompt.technical_instructions ?? "",
    sample_output: prompt.sample_output ?? "",
    response_schema: stringifySchema(prompt.response_schema),
    input_keys: prompt.input_keys ?? [],
  });

  const saveMutation = useMutation({
    mutationFn: async (body: { draft_overrides: DraftOverrides | null }) => {
      const resp = await putPromptDraftRaw(promptKey, body);
      if (resp.status !== 200) {
        const detail = (resp.data as { detail?: string } | undefined)?.detail ?? `HTTP ${resp.status}`;
        throw new Error(detail);
      }
      return resp.data;
    },
    onSuccess: (fresh) => {
      queryClient.setQueryData(draftQueryKey(promptKey), fresh);
      // Clear any stale error from the discard mutation so the shared
      // error banner doesn't surface an old failure alongside fresh success.
      clearMutation.reset();
      // Signal the inputs picker to clear its search box.
      setSaveVersion((v) => v + 1);
    },
  });

  // response_schema/sample_output write straight to the live Prompt
  // row — same endpoint Rio's apply_update_prompt_row calls — never
  // staged in draft_overrides. No "Open PR" step needed for these two;
  // they take effect immediately, same as Rio's direct writes.
  const liveUpdateMutation = useMutation({
    mutationFn: async (body: { response_schema?: unknown; sample_output?: string | null }) => {
      const resp = await updatePromptRaw({
        id: prompt.id,
        name: prompt.name,
        ...body,
      });
      if (resp.status !== 200) {
        const detail = (resp.data as { detail?: string } | undefined)?.detail ?? `HTTP ${resp.status}`;
        throw new Error(detail);
      }
      return resp.data;
    },
    onSuccess: () => {
      // defaultValues reads these two fields straight off the prompt
      // query — refetch it so the form (and every other reader of
      // GetPromptDetailsOutput) sees the new value immediately.
      queryClient.invalidateQueries({ queryKey: getGetPromptDetailsQueryKey(promptKey) });
    },
  });

  const clearMutation = useMutation({
    mutationFn: async () => {
      const resp = await deletePromptDraftRaw(promptKey);
      if (resp.status !== 200) {
        const detail = (resp.data as { detail?: string } | undefined)?.detail ?? `HTTP ${resp.status}`;
        throw new Error(detail);
      }
      return resp.data;
    },
    onSuccess: (fresh) => {
      queryClient.setQueryData(draftQueryKey(promptKey), fresh);
      saveMutation.reset();
      setConfirmingDiscard(false);
      // Reset the form to yaml source now that the draft is gone.
      reset(yamlSourceValues());
    },
  });

  const { formState, RegisteredFormTextArea, reset, onSubmit, setValue, watch, register } = useFormUtils<FormShape>({
    defaultValues,
    mutationFn: async (values) => {
      const dirty = formState.dirtyFields;
      if (Object.keys(dirty).length === 0) {
        notifyError("No changes to save.");
        return;
      }

      // Merge dirty fields into any existing overrides (preserves
      // fields the user hasn't touched this session — the backend
      // REPLACE semantic means we must send the full dict) and route
      // response_schema/sample_output to a direct live-row update
      // instead — see promptDraftFields.ts for why.
      const { nextOverrides, liveUpdate, draftDirty, error } = splitDirtyFields(
        dirty,
        values,
        overrides as Record<string, unknown>,
      );
      if (error) {
        notifyError(error);
        return;
      }

      let draftSaved = false;
      try {
        if (draftDirty) {
          await saveMutation.mutateAsync({ draft_overrides: nextOverrides });
          draftSaved = true;
        }
        if (Object.keys(liveUpdate).length > 0) {
          await liveUpdateMutation.mutateAsync(liveUpdate);
        }
        // Reset the form to the just-saved values so dirty tracking
        // starts fresh from the new baseline.
        reset(values);
      } catch (err) {
        if (draftSaved) {
          // The draft PUT already landed server-side even though the
          // live response_schema/sample_output update below failed.
          // Re-baseline the draft-backed fields (they're saved, so
          // mark them clean) but keep the live fields at their
          // pre-submit baseline via keepValues, so the user's edit
          // there is preserved on screen and still shows as dirty —
          // otherwise "Undo changes" would revert the successfully
          // saved draft fields back to stale values (PROC-190).
          reset(partialFailureResetBaseline(values, liveUpdate, defaultValues), { keepValues: true });
        }
        notifyError(err instanceof Error ? err.message : "Failed to save.");
      }
    },
  });

  // Register fields managed via setValue (not native register).
  useEffect(() => {
    register("role");
    register("templated_instructions");
    register("technical_instructions");
    register("input_keys");
  }, [register]);

  const isDirty = Object.keys(formState.dirtyFields).length > 0;

  // Sync form state to fresh server data when the underlying draft /
  // prompt queries change out-of-band — e.g. when Rio writes to
  // ``draft_overrides`` and the chat drawer invalidates
  // ``["prompt-draft", promptKey]`` / the details query, causing
  // ``draft`` and ``prompt`` props to update. Without this reset,
  // react-hook-form only reads ``defaultValues`` at mount, so the
  // form keeps showing the OLD values until a hard refresh.
  //
  // Guarded by ``!isDirty`` because we must NEVER clobber the user's
  // in-progress edits. If they're typing when Rio writes, the update
  // is dropped for the editor; the query cache still has the fresh
  // data, so the next mount (or discard) picks it up.
  useEffect(() => {
    if (!isDirty) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isDirty
    // is intentionally excluded so this only fires on defaultValues
    // changing (the user typing shouldn't retrigger a form reset).
  }, [defaultValues, reset]);

  // Publish dirty state upward so RunsPanel can warn before running against
  // stale server state (the runner reads the SAVED draft, not the buffer).
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // hasRealDraft ignores stray response_schema/sample_output keys a
  // prompt may still be carrying from before this fix — they're dead
  // weight now (never read, never displayed) and shouldn't make the
  // footer think there's a real pending draft to review/discard.
  const hasSavedDraft = hasRealDraft(draft?.draft_overrides as Record<string, unknown> | null | undefined);
  const isSaving = saveMutation.isPending || liveUpdateMutation.isPending;
  const canSave = isDirty && !isSaving && !clearMutation.isPending;
  const canClear = hasSavedDraft && !isSaving && !clearMutation.isPending;

  const handleReviewDraft = () => {
    if (isDirty) {
      notifyError("Save your changes before reviewing — the diff uses the saved draft.");
      return;
    }
    if (!hasSavedDraft) {
      notifyError("Draft is empty — nothing to review.");
      return;
    }
    navigate(`/prompts/${encodeURIComponent(promptKey)}/review-draft`);
  };

  return (
    <>
      <div className="flex flex-col gap-4 p-8 bg-bg-surface border border-border-medium rounded-xl">
        <Heading size="small">Prompt</Heading>
        <FormContainer onSubmit={onSubmit}>
          <div className="flex flex-col gap-4">
            <CollapsibleField
              label="Role"
              // Role is set on almost every prompt from yaml; forcing it
              // open on landing crowded the panel without adding info.
              // Keep it collapsed by default; the preview line shows the
              // current value so the user can still see it at a glance.
              defaultOpen={false}
              collapsedPreview={defaultValues.role || "(empty)"}
            >
              <PromptRoleSelect
                value={watch("role") ?? ""}
                onChange={(next) => setValue("role", next, { shouldDirty: true })}
              />
            </CollapsibleField>

            <CollapsibleField
              label="Templated Instructions"
              defaultOpen={!!defaultValues.templated_instructions}
              collapsedPreview={defaultValues.templated_instructions || "(empty)"}
            >
              <PromptTemplatedInstructionsSelect
                value={watch("templated_instructions") ?? ""}
                onChange={(next) => setValue("templated_instructions", next, { shouldDirty: true })}
              />
            </CollapsibleField>

            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-text-secondary">Instructions</span>
              {/* Sans-serif for the body: prose reads as writing, not
                  code. whitespace-pre-wrap keeps intentional line breaks
                  in place so bullet lists / numbered steps survive. */}
              <textarea
                {...register("instructions")}
                spellCheck={false}
                className="w-full min-h-[30vh] text-sm leading-relaxed p-4 rounded-lg border border-border-medium bg-bg-default text-text-primary whitespace-pre-wrap focus:outline-none focus:ring-2 focus:ring-limestone-300"
              />
            </div>

            <CollapsibleField
              label="Technical Instructions"
              defaultOpen={!!defaultValues.technical_instructions}
              collapsedPreview={defaultValues.technical_instructions || "(empty)"}
            >
              <PromptTechnicalInstructionsSelect
                value={watch("technical_instructions") ?? ""}
                onChange={(next) => setValue("technical_instructions", next, { shouldDirty: true })}
              />
            </CollapsibleField>

            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-text-secondary">Input Keys</span>
                {/* Quick escape hatch when a needed assessment isn't in
                    the catalog dropdown yet. Opens a bot PR that appends
                    the key under ``inputs.yaml → assessments:`` — after
                    it merges the key becomes selectable here. */}
                <button
                  type="button"
                  onClick={() => setIsNewAssessmentOpen(true)}
                  title="Register a new assessment input via a paired-PR bot"
                  className="ml-auto inline-flex items-center gap-1 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-800 cursor-pointer hover:bg-green-100"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  Assessment
                </button>
              </div>
              <PromptInputsSelect
                // `key` on saveVersion → remount after every successful
                // save, which resets the internal search-query box. The
                // catalog fetch is cached, so remounting is cheap.
                key={saveVersion}
                value={watch("input_keys") ?? []}
                onChange={(next) => setValue("input_keys", next, { shouldDirty: true })}
              />
            </div>

            <CollapsibleField
              label="Sample Output"
              hint="Saves immediately — no PR needed"
              defaultOpen={!!defaultValues.sample_output}
              collapsedPreview={defaultValues.sample_output ? `${defaultValues.sample_output.length} chars` : "(empty)"}
            >
              <RegisteredFormTextArea formKey="sample_output" rows={6} />
            </CollapsibleField>

            <CollapsibleField
              label="Response Schema"
              hint="Saves immediately — no PR needed"
              defaultOpen={!!defaultValues.response_schema}
              collapsedPreview={
                defaultValues.response_schema ? `${defaultValues.response_schema.length} chars` : "(empty)"
              }
            >
              <RegisteredFormTextArea
                formKey="response_schema"
                rows={6}
                placeholder='{"type": "object", "properties": {...}}'
              />
            </CollapsibleField>

            {(saveMutation.isError || liveUpdateMutation.isError || clearMutation.isError) && (
              <span className="text-sm text-red-600">
                {clearMutation.isError ? "Couldn't discard draft — try again." : "Couldn't save — try again."}
              </span>
            )}
          </div>
        </FormContainer>
      </div>

      {confirmingDiscard && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setConfirmingDiscard(false)}
        >
          <div
            className="bg-bg-surface rounded-xl p-6 max-w-md w-full flex flex-col gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <Heading size="small">Discard draft?</Heading>
            <Paragraph colorType="secondary">
              This will permanently delete the current draft (instructions + all structural overrides) and revert the
              editor to the yaml source. Runs already saved in History keep their snapshot, but this draft can&apos;t be
              recovered.
            </Paragraph>
            <div className="flex justify-end gap-2">
              <Button
                text="Cancel"
                appearance="secondary"
                onClick={() => setConfirmingDiscard(false)}
                disabled={clearMutation.isPending}
              />
              <button
                type="button"
                onClick={() => clearMutation.mutate()}
                disabled={clearMutation.isPending}
                className="text-sm px-3 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {clearMutation.isPending ? "Discarding…" : "Discard draft"}
              </button>
            </div>
          </div>
        </div>
      )}

      <TemplateSaveFooter
        isDirty={isDirty}
        isSaving={isSaving}
        onSave={onSubmit}
        // Undo Changes reverts to the last-saved baseline (which is
        // whatever `reset(values)` was last called with — either the
        // useForm defaultValues on mount, or the just-saved values).
        onDiscard={() => reset()}
        saveLabel="Save draft"
        discardLabel="Undo changes"
        saveDisabled={!canSave}
        dangerAction={{
          label: "Discard draft (revert to yaml)",
          onClick: () => setConfirmingDiscard(true),
          disabled: !canClear,
        }}
        promoteAction={
          hasSavedDraft
            ? {
                label: "Review draft →",
                onClick: handleReviewDraft,
              }
            : undefined
        }
      />
      <NewAssessmentInputDialog isDialogOpen={isNewAssessmentOpen} setDialogOpen={setIsNewAssessmentOpen} />
    </>
  );
};
